/**
 * Invite accept page (`/invite/:token`).
 *
 * Seller entry point for the share-and-accept flow. When a buyer shares
 * a `t.me/<bot>?startapp=invite_<token>` link and the seller taps it,
 * Telegram opens the Mini App with `initDataUnsafe.start_param =
 * "invite_<token>"`. The `InviteDeepLinkRedirect` component in
 * `src/routes/router.tsx` reads that param and navigates here.
 *
 * Flow:
 *   1. Fetch the pending invite via `GET /deals/invite/:token`.
 *   2. Show a preview: buyer name, amount, title, description, terms,
 *      funding timeout, expiration countdown.
 *   3. Seller taps "Accept" (Telegram MainButton) → backend calls
 *      `dealService.create(...)` which deploys the child escrow and
 *      returns the new deal row → we redirect to `/deals/:shortId`.
 *   4. Seller taps "Decline" → we navigate back to `/deals` without
 *      consuming the invite (it stays in Redis until TTL expires or
 *      another seller accepts).
 *
 * Error states:
 *   - 404 from GET: invite expired, already accepted, or bad token.
 *     Show a friendly "this link is no longer valid" screen.
 *   - 422 from POST (buyer/seller wallet or ban issue): show the error
 *     message inline; accept button becomes retry.
 *   - Network error: generic retry card.
 *
 * Quality rules (matches peer pages):
 *   - Zero `any`. Uses exported DTO types.
 *   - Zero hardcoded user-facing strings — everything through `useT()`.
 *   - BigInt safety: `invite.amount` is a string → `BigInt()` → `nanoToTon`.
 *   - Haptics on every user action.
 *   - AppShell wrapper, MainButton for the primary accept CTA.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ShieldCheck, Wallet } from 'lucide-react';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { deals as dealsApi, isApiError, users } from '@/lib/api';
import type { PendingInvite } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { useAppStore } from '@/lib/state/store';
import { nanoToTon } from '@/lib/ton/format';
import { formatRelativeTime } from '@/lib/utils/format';
import { haptics } from '@/lib/telegram/haptics';
import { useBackButton, useMainButton } from '@/lib/telegram/webapp';
import { ROUTES } from '@/routes/router';

// ─── Local view helpers ────────────────────────────────────

function formatAmount(nano: string, decimals = 2): string {
  try {
    return `${nanoToTon(BigInt(nano), decimals)} TON`;
  } catch {
    return '— TON';
  }
}

// ─── Component ─────────────────────────────────────────────

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; invite: PendingInvite }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

export function InvitePage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const t = useT();
  const currentUser = useAppStore((s) => s.user);
  const tonAddress = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Back button → deal list.
  useBackButton(
    useCallback(() => {
      navigate(ROUTES.deals);
    }, [navigate]),
  );

  // ── Load the invite ────────────────────────────────────
  const load = useCallback(async (): Promise<void> => {
    if (!token) {
      setState({ kind: 'not-found' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const { invite } = await dealsApi.getInvite(token);
      if (!mountedRef.current) return;
      setState({ kind: 'ready', invite });
    } catch (err) {
      if (!mountedRef.current) return;
      if (isApiError(err) && err.status === 404) {
        setState({ kind: 'not-found' });
        return;
      }
      const message =
        err instanceof Error ? err.message : t('invite.error.loadFailed');
      setState({ kind: 'error', message });
    }
  }, [t, token]);

  useEffect(() => {
    void load();
  }, [load]);

  // Ensure the user has a wallet linked before they can accept. If
  // TonConnect is connected but the backend profile is missing the
  // address, persist it in the background — same pattern as Onboarding.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    if (!tonAddress) return;
    if (currentUser?.tonAddress && currentUser.tonAddress === tonAddress) {
      return;
    }
    void (async (): Promise<void> => {
      try {
        await users.updateWallet({ tonAddress });
      } catch {
        // Non-fatal — the accept call will re-check server-side.
      }
    })();
  }, [currentUser, state, tonAddress]);

  // ── Accept handler ─────────────────────────────────────
  const handleAccept = useCallback(async (): Promise<void> => {
    if (state.kind !== 'ready' || submitting || !token) return;

    // Wallet-connected guard. We can't deploy a contract for a seller
    // who has no TON address on record.
    if (!tonAddress) {
      haptics.warning();
      void tonConnectUI.openSingleWalletModal('tonkeeper');
      return;
    }

    haptics.medium();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { deal } = await dealsApi.acceptInvite(token);
      if (!mountedRef.current) return;
      haptics.success();
      navigate(`/deals/${deal.shortId}`, {
        state: { justAccepted: true },
      });
    } catch (err) {
      if (!mountedRef.current) return;
      haptics.error();
      const message = isApiError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : t('invite.error.acceptFailed');
      setSubmitError(message);
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [navigate, state, submitting, t, token, tonAddress, tonConnectUI]);

  const handleDecline = useCallback((): void => {
    haptics.light();
    navigate(ROUTES.deals);
  }, [navigate]);

  // ── Telegram MainButton ────────────────────────────────
  const mainButtonConfig = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const text = submitting
      ? t('invite.button.accepting')
      : !tonAddress
        ? t('invite.button.connectWallet')
        : t('invite.button.accept');
    return {
      text,
      onClick: () => void handleAccept(),
      loading: submitting,
    };
  }, [handleAccept, state, submitting, t, tonAddress]);

  useMainButton(mainButtonConfig);

  // ── Render ────────────────────────────────────────────
  if (state.kind === 'loading') {
    return (
      <AppShell>
        <PageHeader title={t('invite.title')} />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </AppShell>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <AppShell>
        <PageHeader title={t('invite.title')} />
        <Card className="mt-6 flex flex-col items-center gap-4 p-6 text-center">
          <AlertTriangle
            className="h-12 w-12 text-warning"
            aria-hidden
          />
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {t('invite.notFound.title')}
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              {t('invite.notFound.body')}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleDecline}>
            {t('invite.backToDeals')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <AppShell>
        <PageHeader title={t('invite.title')} />
        <Card className="mt-6 flex flex-col items-center gap-4 p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-danger" aria-hidden />
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {t('invite.error.loadFailed')}
            </h2>
            <p className="mt-1 break-words text-sm text-text-secondary">
              {state.message}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            {t('common.tryAgain')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  // ── Ready state — show the invite preview ─────────────
  const { invite } = state;
  const amountDisplay = formatAmount(invite.amount, 4);
  const expiresRelative = formatRelativeTime(invite.expiresAt);
  const isSelfInvite =
    currentUser?.id === invite.buyerId ||
    (currentUser?.telegramId &&
      currentUser.telegramId === invite.buyerTelegramId);

  return (
    <AppShell>
      <PageHeader
        title={t('invite.title')}
        description={t('invite.subtitle')}
      />

      {/* Hero card — big amount + title + buyer */}
      <Card className="mt-4 flex flex-col gap-4 border-accent-gold/30 bg-bg-glass/60 p-5 backdrop-blur-md">
        <div>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            {t('invite.hero.from')}
          </p>
          <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-text-primary">
            <Wallet className="h-4 w-4 text-accent-gold" aria-hidden />
            {invite.buyerName}
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            {t('invite.hero.amount')}
          </p>
          <p className="mt-1 bg-gradient-to-r from-accent-gold to-accent-purple bg-clip-text text-4xl font-bold text-transparent">
            {amountDisplay}
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            {t('invite.hero.for')}
          </p>
          <p className="mt-1 break-words text-base font-medium text-text-primary">
            {invite.title}
          </p>
        </div>
      </Card>

      {/* Details card */}
      <Card className="mt-4 flex flex-col gap-3 p-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            {t('invite.details.description')}
          </h3>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-text-secondary">
            {invite.description}
          </p>
        </div>

        {invite.terms && (
          <div className="border-t border-border/30 pt-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              {t('invite.details.terms')}
            </h3>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-text-secondary">
              {invite.terms}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 border-t border-border/30 pt-3 text-xs text-text-muted">
          <Badge variant="muted" className="capitalize">
            {t(`newDeal.category.${invite.category}` as const)}
          </Badge>
          <span>
            {t('invite.details.fundingWindow', { hours: invite.timeoutHours })}
          </span>
          <span>·</span>
          <span>{t('invite.details.expires', { relative: expiresRelative })}</span>
        </div>
      </Card>

      {/* Disclaimer */}
      <Card className="mt-4 flex items-start gap-3 border-warning/30 bg-warning/10 p-4">
        <ShieldCheck
          className="mt-0.5 h-5 w-5 shrink-0 text-warning"
          aria-hidden
        />
        <p className="text-xs leading-relaxed text-text-secondary">
          {t('invite.disclaimer')}
        </p>
      </Card>

      {/* Self-invite guard */}
      {isSelfInvite && (
        <Card className="mt-4 flex items-start gap-3 border-danger/30 bg-danger/10 p-4">
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0 text-danger"
            aria-hidden
          />
          <p className="text-xs leading-relaxed text-text-secondary">
            {t('invite.error.selfInvite')}
          </p>
        </Card>
      )}

      {/* Submit error banner */}
      {submitError && (
        <Card className="mt-4 flex items-start gap-3 border-danger/30 bg-danger/10 p-4">
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0 text-danger"
            aria-hidden
          />
          <p className="break-words text-xs leading-relaxed text-text-secondary">
            {submitError}
          </p>
        </Card>
      )}

      {/* Decline secondary action (primary is the Telegram MainButton) */}
      <div className="mt-6 flex justify-center pb-4">
        <button
          type="button"
          onClick={handleDecline}
          className="text-xs font-medium text-text-muted underline-offset-4 hover:text-text-secondary hover:underline"
        >
          {t('invite.button.decline')} →
        </button>
      </div>
    </AppShell>
  );
}

